import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

const ACTIONS = [
  {
    name: 'Update invoice to match order',
    invoice: 'Rewrites it. This can\'t be undone.',
    owed: 'Follows the order, except shipping and lines missing from the invoice',
    warning: 'Usually goes away. Anything it can\'t update stays, and it tells you what.',
    when: 'Only before the invoice is sent, with no payments on it.',
  },
  {
    name: 'Raise credit/debit note',
    invoice: 'No. Adds a separate note.',
    owed: 'Yes',
    warning: 'Stays until you keep the invoice',
    when: 'When the invoice has been sent or has payments.',
  },
  {
    name: 'Keep invoice as it is',
    invoice: 'No',
    owed: 'No',
    warning: 'Clears. Comes back if the order changes again.',
    when: 'When the difference is right, or a note has dealt with it. You can undo this.',
  },
  {
    name: 'Flag for correction',
    invoice: 'No',
    owed: 'No',
    warning: 'Stays',
    when: 'When it needs fixing later, or someone else needs to look at it.',
  },
];

function Section({ title, children }) {
  return (
    <Box className="mb-20">
      <Typography variant="subtitle2" className="mb-4">{title}</Typography>
      {children}
    </Box>
  );
}

/** PGSS-5862: what the invoice discrepancy panel's difference means and what each action does. */
function InvoiceDiscrepancyHelpDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="discrepancy-help">
      <DialogTitle sx={{ pr: 6 }}>
        When an invoice doesn&apos;t match its order
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Section title="What this means">
          <Typography variant="body2">
            The order changed after the invoice was made, or the invoice was built with different figures. Either side
            could be the right one, so check the order first. The panel only compares them.
          </Typography>
        </Section>

        <Section title="What each action does">
          <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Changes the invoice?</TableCell>
                  <TableCell>Changes what&apos;s owed?</TableCell>
                  <TableCell>Warning</TableCell>
                  <TableCell>Use it</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ACTIONS.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell><strong>{row.name}</strong></TableCell>
                    <TableCell>{row.invoice}</TableCell>
                    <TableCell>{row.owed}</TableCell>
                    <TableCell>{row.warning}</TableCell>
                    <TableCell>{row.when}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>

        <Section title="Credit or debit?">
          <Typography variant="body2">
            If the order is right and the invoice is higher, the customer was billed too much: raise a credit note, and
            they owe less. If the order is right and the invoice is lower, raise a debit note, and they owe more. The
            panel suggests a type and amount on that basis. If the invoice is the right one, no note is needed: keep it
            as it is, and correct the order if it needs it.
          </Typography>
        </Section>

        <Section title="Fixing an invoice that has been sent">
          <Typography component="ol" variant="body2" sx={{ pl: 2.5, listStyle: 'decimal' }}>
            <li>Check the order and decide which figure is right.</li>
            <li>Raise the credit or debit note, if what the customer owes needs to change.</li>
            <li>
              Choose Keep invoice as it is. A note doesn&apos;t change either side, so the difference stays until you do.
            </li>
          </Typography>
        </Section>
      </DialogContent>
    </Dialog>
  );
}

export default InvoiceDiscrepancyHelpDialog;
